import * as Api from '@utils/Api';

import { all, call, put } from 'redux-saga/effects';

import { types as memberTypes } from '@reducers/organizationMembers';
import { types as organizationTypes } from '@reducers/organizations';
import { types as billingTypes } from '@reducers/billing';
import { Type as accountTypes } from '../reducers/Account';
import { fetchBillingInfo } from './billing';

export function* fetchDetailedOrganization(action) {
  const { uuid } = action;
  if (!uuid) {
    throw new Error('Missing required parameters for fetching an organization');
  }
  const response = yield call(Api.get, `organizations/${uuid}`);
  const data = response.data;
  if (!response.success) {
    yield put({
      type: organizationTypes.ORGANIZATION_FAILURE,
      error: data ? data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: organizationTypes.ORGANIZATION_SUCCESS,
      uuid,
      organization: data,
    });
  }
}

export function* fetchOrganizationInvoiceHistory(action) {
  const { uuid } = action;
  if (!uuid) {
    throw new Error('Missing required parameters for fetching invoices');
  }

  const response = yield call(
    Api.get,
    `organizations/${uuid}/billing/invoice-history`
  );
  const data = response.data;
  if (!response.success) {
    yield put({
      type: organizationTypes.INVOICE_FAILURE,
      error: data ? data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: organizationTypes.INVOICE_SUCCESS,
      invoices: data.data,
    });
  }
}

export function* fetchMissingMembers(action) {
  const { organizationUuid } = action;
  if (!organizationUuid) {
    throw new Error('Missing required parameters for fetching missing members');
  }
  const response = yield call(
    Api.get,
    `organizations/${organizationUuid}/missing-members`
  );
  const data = response.data;
  if (!response.success) {
    yield put({
      type: memberTypes.MISSING_MEMBERS_FAILURE,
      error: data ? data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: memberTypes.MISSING_MEMBERS_SUCCESS,
      organizationUuid,
      missingMembers: data,
    });
  }
}

export function* updateOrganizationBillingPlan(action) {
  const { plan, organizationUuid } = action;
  if (!plan) {
    throw new Error('Missing required parameters for updating billing plan');
  }
  const response = yield call(
    Api.put,
    `organizations/${organizationUuid}/billing/plan`,
    {
      plan,
      organizationUuid,
    }
  );
  const data = response.data;
  if (!response.success) {
    yield put({
      type: organizationTypes.UPDATE_BILLING_PLAN_FAILURE,
      error: data ? data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: organizationTypes.UPDATE_BILLING_PLAN_SUCCESS,
    });
    yield fetchDetailedOrganization({ uuid: organizationUuid });
  }
}

export function* initializeCodeReviewSubscription(action) {
  const { organizationUuid } = action;
  const response = yield call(
    Api.post,
    `organizations/${organizationUuid}/subscription`,
    { organizationUuid }
  );
  const data = response.data;
  if (!response.success) {
    yield put({
      type: organizationTypes.SUBSCRIPTION_CODE_REVIEW_FAILURE,
      error: data ? data.error_message : 'Unknown error',
    });
  } else {
    yield all(
      yield put({
        type: organizationTypes.SUBSCRIPTION_CODE_REVIEW_SUCCESS,
      }),
      yield put({ type: accountTypes.FETCH_ACCOUNT_REQUEST }),
      yield put({
        type: billingTypes.GET_BILLING_INFO_REQUEST,
        organizationUUID: organizationUuid,
      })
    );
  }
}

export function* initializeStaticSubscription(action) {
  const { organizationUUID } = action;
  if (!organizationUUID) {
    throw new Error('Missing required parameters for static subscription.');
  }

  const response = yield call(
    Api.post,
    `organizations/${organizationUUID}/static-subscription`
  );
  const data = response.data;
  if (!response.success) {
    yield put({
      type: organizationTypes.SUBSCRIPTION_STATIC_FAILURE,
      error: data ? data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: organizationTypes.SUBSCRIPTION_STATIC_SUCCESS,
    });
    yield put({ type: accountTypes.FETCH_ACCOUNT_REQUEST });
  }
}

export function* renewCodeReviewSubscription(action) {
  const { organizationUuid } = action;
  const response = yield call(
    Api.post,
    `organizations/${organizationUuid}/subscription/reactivate`,
    {
      organizationUuid,
    }
  );
  const data = response.data;
  if (!response.success) {
    yield put({
      type: organizationTypes.SUBSCRIPTION_CODE_REVIEW_RENEW_FAILURE,
      error: data ? data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: organizationTypes.SUBSCRIPTION_CODE_REVIEW_RENEW_SUCCESS,
    });
    yield all(
      yield put({ type: accountTypes.FETCH_ACCOUNT_REQUEST }),
      yield put({
        type: billingTypes.GET_BILLING_INFO_REQUEST,
        organizationUUID: organizationUuid,
      })
    );
  }
}

export function* renewStaticSubscription(action) {
  const { organizationUuid } = action;
  const response = yield call(
    Api.post,
    `organizations/${organizationUuid}/static-subscription/reactivate`,
    { organizationUuid }
  );
  const data = response.data;
  if (!response.success) {
    yield put({
      type: organizationTypes.SUBSCRIPTION_STATIC_RENEW_FAILURE,
      error: data ? data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: organizationTypes.SUBSCRIPTION_STATIC_RENEW_SUCCESS,
    });
    yield put({ type: accountTypes.FETCH_ACCOUNT_REQUEST });
    yield fetchBillingInfo({ organizationUUID: organizationUuid });
  }
}

export function* cancelCodeReviewSubscription(action) {
  const { organizationUuid } = action;
  const response = yield call(
    Api.del,
    `organizations/${organizationUuid}/subscription`,
    {
      organizationUuid,
    }
  );
  const data = response.data;
  if (!response.success) {
    yield put({
      type: organizationTypes.SUBSCRIPTION_CODE_REVIEW_CANCEL_FAILURE,
      error: data ? data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: organizationTypes.SUBSCRIPTION_CODE_REVIEW_CANCEL_SUCCESS,
    });
    yield all(
      yield put({ type: accountTypes.FETCH_ACCOUNT_REQUEST }),
      yield put({
        type: billingTypes.GET_BILLING_INFO_REQUEST,
        organizationUUID: organizationUuid,
      })
    );
  }
}

export function* cancelStaticSubscription(action) {
  const { organizationUuid } = action;
  const response = yield call(
    Api.del,
    `organizations/${organizationUuid}/static-subscription`,
    { organizationUuid }
  );

  const data = response.data;
  if (!response.success) {
    yield put({
      type: organizationTypes.SUBSCRIPTION_STATIC_CANCEL_FAILURE,
      error: data ? data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: organizationTypes.SUBSCRIPTION_STATIC_CANCEL_SUCCESS,
    });
    yield put({ type: accountTypes.FETCH_ACCOUNT_REQUEST });
    yield fetchBillingInfo({ organizationUUID: organizationUuid });
  }
}

export function* toggleNotesLock(action) {
  const { organizationUUID, enabled } = action;
  if (!organizationUUID || enabled === undefined) {
    throw new Error('Missing required parameters for toggling notes lock');
  }
  const response = yield call(
    Api.post,
    `organizations/${organizationUUID}/toggle-notes-lock`,
    { enabled }
  );
  if (!response.success) {
    yield put({
      type: organizationTypes.TOGGLE_NOTES_LOCK_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
      errorCode: response.status,
    });
  } else {
    yield put({
      type: organizationTypes.TOGGLE_NOTES_LOCK_SUCCESS,
      organizationUUID,
      enabled,
    });
    yield fetchDetailedOrganization({ uuid: organizationUUID });
  }
}

export function* toggleFullRepoSearch(action) {
  const { organizationUUID, enabled } = action;
  if (!organizationUUID || enabled === undefined) {
    throw new Error(
      'Missing required parameters for toggling full repository search visibility'
    );
  }
  const response = yield call(
    Api.post,
    `organizations/${organizationUUID}/toggle-repository-search`,
    { enabled }
  );
  if (!response.success) {
    yield put({
      type: organizationTypes.TOGGLE_FULL_REPO_SEARCH_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
      errorCode: response.status,
    });
  } else {
    yield put({
      type: organizationTypes.TOGGLE_FULL_REPO_SEARCH_SUCCESS,
      organizationUUID,
      enabled,
    });
    yield fetchDetailedOrganization({ uuid: organizationUUID });
  }
}

export function* inviteMissingMembers(action) {
  const { invites, organizationUuid } = action;
  if (!invites) {
    throw new Error('Missing required parameters for inviting missing members');
  }
  // Iterates through list of missing members with valid email addresses to send them each an invite
  const response = yield call(
    Api.post,
    `organizations/${organizationUuid}/invite-members`,
    invites
  );
  const data = response.data;
  if (!response.success) {
    yield put({
      type: memberTypes.INVITE_MISSING_MEMBERS_FAILURE,
      error: data ? data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: memberTypes.INVITE_MISSING_MEMBERS_SUCCESS,
    });
    yield fetchMissingMembers({ organizationUuid: organizationUuid });
  }
}

export function* updateOrganizationBillingInfo(action) {
  const { token, organizationUUID } = action;
  if (!token || !organizationUUID) {
    throw new Error('Missing required parameters for updating billing info');
  }
  const response = yield call(
    Api.put,
    `organizations/${organizationUUID}/billing/info`,
    { token }
  );
  const data = response.data;
  if (!response.success) {
    yield put({
      type: organizationTypes.UPDATE_BILLING_INFO_FAILURE,
      error: data ? data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: organizationTypes.UPDATE_BILLING_INFO_SUCCESS,
    });
    yield fetchBillingInfo({ organizationUUID });
  }
}

export function* updateOrganizationProduct(action) {
  const { productName, organizationUUID, enabled } = action;
  if (!productName) {
    throw new Error('Missing required parameters for product update.');
  }

  const response = yield call(
    Api.put,
    `organizations/${organizationUUID}/products/${productName}`,
    { enabled }
  );
  const data = response.data;
  if (!response.success) {
    yield put({
      type: organizationTypes.ORGANIZATION_PRODUCT_FAILURE,
      error: data ? data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: organizationTypes.ORGANIZATION_PRODUCT_SUCCESS,
    });
    yield put({ type: accountTypes.FETCH_ACCOUNT_REQUEST });
    yield fetchBillingInfo({ organizationUUID });
  }
}

export function* markProjectsOnboarded(action) {
  const { organizationUUID } = action;
  if (!organizationUUID) {
    throw new Error(
      'Missing required parameters for marking projects as onboarded'
    );
  }
  const response = yield call(
    Api.post,
    `organizations/${organizationUUID}/mark-onboarding-complete`
  );
  if (!response.success) {
    yield put({
      type: organizationTypes.MARK_PROJECTS_ONBOARDED_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
      errorCode: response.status,
    });
  } else {
    yield put({
      type: organizationTypes.MARK_PROJECTS_ONBOARDED_SUCCESS,
    });
    yield put({ type: accountTypes.FETCH_ACCOUNT_REQUEST });
  }
}
